import React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface LoadingProps {
  /**
   * 加载提示文字
   */
  tip?: string;
  /**
   * 加载器大小
   */
  size?: 'small' | 'default' | 'large';
  /**
   * 是否显示加载状态
   */
  spinning?: boolean;
  /**
   * 自定义样式类名
   */
  className?: string;
  /**
   * 子组件（当 spinning 为 false 时显示）
   */
  children?: React.ReactNode;
}

/**
 * 通用加载组件
 * 主要用于路由懒加载时的加载状态展示
 */
const Loading: React.FC<LoadingProps> = ({
  tip = '加载中...',
  size = 'default',
  spinning = true,
  className,
  children,
}) => {
  // 根据 size 确定图标大小
  const iconSizeMap = {
    small: 'h-4 w-4',
    default: 'h-6 w-6',
    large: 'h-8 w-8',
  };

  const iconSize = iconSizeMap[size];

  if (children) {
    // 如果有子组件，则作为包装器使用
    return (
      <div className="relative">
        {spinning && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-2">
              <Loader2 className={cn('animate-spin text-primary', iconSize)} />
              {tip && <span className="text-sm text-muted-foreground">{tip}</span>}
            </div>
          </div>
        )}
        {children}
      </div>
    );
  }

  // 默认的居中加载状态
  return (
    <div
      className={cn('flex flex-col items-center justify-center min-h-[200px] w-full', className)}
    >
      <div className="flex flex-col items-center gap-2">
        <Loader2 className={cn('animate-spin text-primary', iconSize)} />
        {tip && <span className="text-sm text-muted-foreground">{tip}</span>}
      </div>
    </div>
  );
};

/**
 * 页面级加载组件
 * 用于路由懒加载时的全屏加载状态
 */
export const PageLoading: React.FC<{ tip?: string }> = ({ tip = '页面加载中...' }) => {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] w-full">
      <div className="flex flex-col items-center gap-2">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="text-sm text-muted-foreground">{tip}</span>
      </div>
    </div>
  );
};

/**
 * 内容加载组件
 * 用于局部内容的加载状态
 */
export const ContentLoading: React.FC<{ tip?: string; height?: string }> = ({
  tip = '内容加载中...',
  height = 'h-[200px]',
}) => {
  return (
    <div className={cn('flex flex-col items-center justify-center w-full', height)}>
      <div className="flex flex-col items-center gap-2">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <span className="text-sm text-muted-foreground">{tip}</span>
      </div>
    </div>
  );
};

export default Loading;
